import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Chip, TextField, Checkbox, FormControlLabel, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import api from '../services/api';

export const ReplenishmentPage: React.FC = () => {
  const navigate = useNavigate();
  const [productSug, setProductSug] = useState<any[]>([]);
  const [materialSug, setMaterialSug] = useState<any[]>([]);
  const [capacity, setCapacity] = useState<any[]>([]);
  const [costs, setCosts] = useState<any[]>([]);
  const [openPo, setOpenPo] = useState<{ open: boolean; item?: any; kind: 'producto'|'insumo' }>({ open: false, kind: 'producto' });
  const [simpleMode, setSimpleMode] = useState<boolean>(true); // cards vs tabla
  const [onlyAlerts, setOnlyAlerts] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [supplierFilter, setSupplierFilter] = useState<number | ''>('');

  useEffect(() => {
    (async () => {
      try { const s = await api.getPurchaseSuggestions(); setProductSug(s.items || []); } catch {}
      try { const s = await api.getMaterialSuggestions(); setMaterialSug(s.items || []); } catch {}
      try { const c = await api.getProductionCapacity(); setCapacity(c.items || []); } catch {}
      try { const c = await api.getProductionCosts(); setCosts(c.items || []); } catch {}
      try { const sps = await api.getSuppliers({ page: 1, pageSize: 500 }); setSuppliers(sps.suppliers || []); } catch {}
    })();
  }, []);

  const filterItems = <T extends any>(items: T[], kind: 'producto'|'insumo') => {
    const q = (search || '').toLowerCase();
    return (items || []).filter((it: any) => {
      const txt = kind === 'producto' ? `${it.productName} ${it.sku}`.toLowerCase() : `${it.name} ${it.code || ''}`.toLowerCase();
      if (q && !txt.includes(q)) return false;
      if (onlyAlerts) {
        const isAlert = (it.suggestedQty || 0) > 0 || (it.reorderPoint !== undefined && (it.currentStock || 0) <= (it.reorderPoint || 0));
        if (!isAlert) return false;
      }
      if (supplierFilter) {
        if ((it.supplierName || '') === '') return false;
        // simple match by name since API doesn’t return supplierId for all
        const sup = suppliers.find(s => s.id === supplierFilter);
        if (!sup) return false;
        if ((it.supplierName || '').toLowerCase() !== (sup.name || '').toLowerCase()) return false;
      }
      return true;
    });
  };

  const exportCsv = (rows: string[][], filename: string) => {
    const csv = rows.map(r => r.map(x => (x ?? '').toString().replace(/"/g, '""')).map(x => `"${x}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
  };

  const productRowsCsv = () => {
    const items = filterItems(productSug, 'producto');
    const rows = [ ['producto','sku','stock','reorden','sugerido','proveedor','lead_dias'] as string[] ];
    for (const it of items) rows.push([ it.productName, it.sku, String(it.currentStock||0), String(it.reorderPoint||0), String(it.suggestedQty||0), it.supplierName||'', String(it.supplierLeadTimeDays ?? '') ]);
    exportCsv(rows, 'reabastecimiento-productos.csv');
  };
  const materialRowsCsv = () => {
    const items = filterItems(materialSug, 'insumo');
    const rows = [ ['insumo','codigo','stock','reorden','sugerido','proveedor','lead_dias'] as string[] ];
    for (const it of items) rows.push([ it.name, it.code||'', String(it.currentStock||0), String(it.reorderPoint||0), String(it.suggestedQty||0), it.supplierName||'', String(it.supplierLeadTimeDays ?? '') ]);
    exportCsv(rows, 'reabastecimiento-insumos.csv');
  };

  const supOptions = useMemo(() => [{ id: 0, name: '— Todos —' }, ...suppliers], [suppliers]);

  const unitCostMap = useMemo(() => new Map(costs.map(c => [c.productId, c.unitCost])), [costs]);

  return (
    <Box sx={{ py: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Reabastecimiento</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
          <FormControlLabel control={<Checkbox checked={onlyAlerts} onChange={e => setOnlyAlerts(e.target.checked)} />} label="Solo alertas" />
          <TextField size="small" placeholder="Buscar producto/insumo" value={search} onChange={e => setSearch(e.target.value)} />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Proveedor</InputLabel>
            <Select
              label="Proveedor"
              value={supplierFilter === '' ? '' : String(supplierFilter)}
              onChange={(e: SelectChangeEvent<string>) => {
                const value = e.target.value as string;
                setSupplierFilter(value === '' ? '' : Number(value));
              }}
            >
              {supOptions.map(s => <MenuItem key={s.id} value={s.id === 0 ? '' : s.id}>{s.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button size="small" variant={simpleMode ? 'contained' : 'outlined'} onClick={() => setSimpleMode(true)}>Cards</Button>
          <Button size="small" variant={!simpleMode ? 'contained' : 'outlined'} onClick={() => setSimpleMode(false)}>Tabla</Button>
        </Stack>
      </Stack>
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 1 }}>
              <Typography variant="h6" gutterBottom>Productos para comprar o fabricar</Typography>
              <Button size="small" variant="outlined" onClick={productRowsCsv}>Exportar CSV</Button>
            </Stack>
            {simpleMode ? (
              <Stack direction={{ xs: 'column', md: 'row' }} sx={{ flexWrap: 'wrap', gap: 2 }}>
                {filterItems(productSug, 'producto').map((s, idx) => {
                  const isAlert = (s.suggestedQty || 0) > 0 || (s.currentStock || 0) <= (s.reorderPoint || 0);
                  const color = isAlert ? ( (s.currentStock || 0) <= 0 ? 'error' : 'warning') : 'success';
                  return (
                    <Card key={idx} sx={{ width: { xs: '100%', sm: 360 } }}>
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" fontWeight={600}>{s.productName}</Typography>
                          <Chip size="small" color={color as any} label={isAlert ? (color==='error'?'CRÍTICO':'ALERTA') : 'OK'} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">SKU: {s.sku}</Typography>
                        <Stack direction="row" spacing={2} sx={{ my: 1 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Stock</Typography>
                            <Typography>{s.currentStock}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Reorden</Typography>
                            <Typography>{s.reorderPoint}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Sugerido</Typography>
                            <Typography fontWeight={700}>{s.suggestedQty}</Typography>
                          </Box>
                        </Stack>
                        <Typography variant="body2">Proveedor: {s.supplierName || '-'}</Typography>
                        <Typography variant="body2" color="text.secondary">Lead: {s.supplierLeadTimeDays ?? '-'} días</Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setOpenPo({ open: true, item: s, kind: 'producto' })}
                          >
                            Orden de compra
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate('/production-orders')}
                          >
                            Orden de fabricación
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
                {filterItems(productSug, 'producto').length === 0 && (
                  <Typography variant="body2" color="text.secondary">No hay productos para mostrar.</Typography>
                )}
              </Stack>
            ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell>Stock</TableCell>
                    <TableCell>Punto de reorden</TableCell>
                    <TableCell align="right">Cantidad sugerida</TableCell>
                    <TableCell>Proveedor</TableCell>
                    <TableCell>Tiempo de entrega (días)</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filterItems(productSug, 'producto').map((s, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{s.productName} ({s.sku})</TableCell>
                      <TableCell>{s.currentStock}</TableCell>
                      <TableCell>{s.reorderPoint}</TableCell>
                      <TableCell align="right">{s.suggestedQty}</TableCell>
                      <TableCell>{s.supplierName || '-'}</TableCell>
                      <TableCell>{s.supplierLeadTimeDays ?? '-'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setOpenPo({ open: true, item: s, kind: 'producto' })}
                          >
                            Crear orden de compra
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate('/production-orders')}
                          >
                            Crear orden de fabricación
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filterItems(productSug, 'producto').length === 0 && (
                    <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No hay sugerencias de productos.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>) }
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 1 }}>
              <Typography variant="h6" gutterBottom>Insumos para reponer</Typography>
              <Button size="small" variant="outlined" onClick={materialRowsCsv}>Exportar CSV</Button>
            </Stack>
            {simpleMode ? (
              <Stack direction={{ xs: 'column', md: 'row' }} sx={{ flexWrap: 'wrap', gap: 2 }}>
                {filterItems(materialSug, 'insumo').map((s, idx) => {
                  const isAlert = (s.suggestedQty || 0) > 0 || (s.currentStock || 0) <= (s.reorderPoint || 0);
                  const color = isAlert ? ( (s.currentStock || 0) <= 0 ? 'error' : 'warning') : 'success';
                  return (
                    <Card key={idx} sx={{ width: { xs: '100%', sm: 360 } }}>
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" fontWeight={600}>{s.name}</Typography>
                          <Chip size="small" color={color as any} label={isAlert ? (color==='error'?'CRÍTICO':'ALERTA') : 'OK'} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">Código: {s.code || '-'}</Typography>
                        <Stack direction="row" spacing={2} sx={{ my: 1 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Stock</Typography>
                            <Typography>{s.currentStock}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Reorden</Typography>
                            <Typography>{s.reorderPoint}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Sugerido</Typography>
                            <Typography fontWeight={700}>{s.suggestedQty}</Typography>
                          </Box>
                        </Stack>
                        <Typography variant="body2">Proveedor: {s.supplierName || '-'}</Typography>
                        <Typography variant="body2" color="text.secondary">Lead: {s.supplierLeadTimeDays ?? '-'} días</Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button size="small" variant="outlined" onClick={() => setOpenPo({ open: true, item: s, kind: 'insumo' })}>Orden de compra</Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
                {filterItems(materialSug, 'insumo').length === 0 && (
                  <Typography variant="body2" color="text.secondary">No hay insumos para mostrar.</Typography>
                )}
              </Stack>
            ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Insumo</TableCell>
                    <TableCell>Stock</TableCell>
                    <TableCell>Punto de reorden</TableCell>
                    <TableCell align="right">Cantidad sugerida</TableCell>
                    <TableCell>Proveedor</TableCell>
                    <TableCell>Tiempo de entrega (días)</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filterItems(materialSug, 'insumo').map((s, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{s.name} {s.code ? `(${s.code})` : ''}</TableCell>
                      <TableCell>{s.currentStock}</TableCell>
                      <TableCell>{s.reorderPoint}</TableCell>
                      <TableCell align="right">{s.suggestedQty}</TableCell>
                      <TableCell>{s.supplierName || '-'}</TableCell>
                      <TableCell>{s.supplierLeadTimeDays ?? '-'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" onClick={() => setOpenPo({ open: true, item: s, kind: 'insumo' })}>Crear orden de compra</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filterItems(materialSug, 'insumo').length === 0 && (
                    <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No hay sugerencias de insumos.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>) }
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Capacidad de producción (estimada)</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Unidades que se pueden fabricar</TableCell>
                    <TableCell align="right">Costo unitario estimado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {capacity.map((c, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{c.productName}</TableCell>
                      <TableCell align="right">{c.buildable}</TableCell>
                      <TableCell align="right">{unitCostMap.has(c.productId) ? `$${unitCostMap.get(c.productId).toLocaleString()}` : '-'}</TableCell>
                    </TableRow>
                  ))}
                  {capacity.length === 0 && (
                    <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary">No hay datos de formulaciones para calcular capacidad.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Stack>

      {/* Modal simple para creación de orden de compra (solo UI) */}
      <Dialog open={openPo.open} onClose={() => setOpenPo({ open: false, kind: 'producto' })} maxWidth="sm" fullWidth>
        <DialogTitle>Crear orden de compra</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Esta orden estará prellenada con la cantidad sugerida. La confirmación y envío a proveedor se agregarán en la siguiente iteración.
          </Typography>
          <Stack spacing={1}>
            <Typography>Ítem: {openPo.item?.productName || openPo.item?.name}</Typography>
            <Typography>Cantidad sugerida: {openPo.item?.suggestedQty}</Typography>
            <Typography>Proveedor: {openPo.item?.supplierName || '-'}</Typography>
            <Typography>Tiempo de entrega: {openPo.item?.supplierLeadTimeDays ?? '-'} días</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPo({ open: false, kind: 'producto' })}>Cerrar</Button>
          <Button variant="contained" disabled>Guardar (próximamente)</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
